import React from 'react';
import { useField } from 'formik';
import { ChangeEvent } from 'react';
import {
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  FormFeedback,
  FormText,
  Label,
} from 'reactstrap';
import { useTranslator } from '../I18n/useTranslator';

interface IFormikNumberInput {
  id?: string;
  name: string;
  unit?: string;
  label?: string;
  min?: number;
  max?: number;
  text?: string;
  step?: string;
  invalid?: boolean;
  innerRef?: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  translateErrMsg?: boolean;
  onClick?: () => void;
  onBlur?: () => void;
  callbackOnChange?: (value: number) => void;
}

const FormikNumberInput: React.FC<IFormikNumberInput> = ({
  unit,
  label,
  text,
  translateErrMsg = false,
  name,
  ...props
}) => {
  // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
  // which we can spread on <input>. We can use field meta to show an error
  // message if the field is invalid and it has been touched (i.e. visited)
  const [field, meta, helpers] = useField({ name, type: 'number' });
  const { t } = useTranslator();

  if (meta.value === 0) {
    helpers.setValue('');
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (props.step) {
      if (e.target.value === '') {
        helpers.setValue('');
      } else {
        const number = +value;
        switch (props.step) {
          case '1':
            helpers.setValue(Math.round(number));
            break;
          case '0.1':
            helpers.setValue(Math.round(number * 10) / 10);
            break;
          case '0.01':
            helpers.setValue(Math.round(number * 100) / 100);
            break;
          case '0.001':
            helpers.setValue(Math.round(number * 1000) / 1000);
            break;
          default:
            helpers.setValue(number);
        }
      }
    } else {
      helpers.setValue(+value);
    }
    if (props.callbackOnChange) props.callbackOnChange(+value);
  };

  const handleKeyDown = (e: any) => {
    // enter
    if (e.keyCode === 13) {
      const nextStepBtn = document.getElementById('next-step-btn');
      nextStepBtn && nextStepBtn.click();
    }
  };

  // chat GPT solution
  let styleObj: Record<string, string> = { '--value': `0ch` };
  if (field.value) {
    styleObj = {
      '--value': `${field.value?.toString().length}ch`,
    };
  }

  return (
    <FormGroup>
      {label && <Label for={props.id || name}>{label}</Label>}
      <InputGroup>
        <Input
          {...field}
          {...props}
          type="number"
          invalid={props.invalid || !!(meta.touched && meta.error)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          style={styleObj}
        />
        {unit && <InputGroupText>{unit}</InputGroupText>}
        {meta.touched && meta.error && (
          <FormFeedback>{translateErrMsg ? t(meta.error) : meta.error}</FormFeedback>
        )}
        {text && !(meta.touched && meta.error) && !meta.value && (
          <FormText className="w-100">{text}</FormText>
        )}
      </InputGroup>
    </FormGroup>
  );
};

export default FormikNumberInput;
